import type { CodeItem } from "@/ai-coding-outputs/admin-coding/codeComparison";
import { useAddToGoldenDataset } from "@/ai-coding-outputs/admin-coding/useAddToGoldenDataset";
import { useGoldenDatasetStatus } from "@/ai-coding-outputs/admin-coding/useGoldenDatasetStatus";
import { useRemoveFromGoldenDataset } from "@/ai-coding-outputs/admin-coding/useRemoveFromGoldenDataset";
import type { GoldenDatasetSource } from "@/api/reviews";
import { useAuth } from "@/auth/AuthProvider";
import { USER_ROLES } from "@/auth/userRoles";

interface UseGoldenDatasetArgs {
  encounterId: string;
  source: GoldenDatasetSource | undefined;
  supported: boolean;
  diagnoses: CodeItem[];
  procedures: CodeItem[];
  onCodesChanged?: () => void;
}

export const useGoldenDataset = ({
  encounterId,
  source,
  supported,
  diagnoses,
  procedures,
  onCodesChanged,
}: UseGoldenDatasetArgs) => {
  const { role } = useAuth();
  const isCoderAdmin = role === USER_ROLES.admin || role === USER_ROLES.clinical_coder_admin;
  const add = useAddToGoldenDataset({ onCodesChanged });
  const remove = useRemoveFromGoldenDataset();
  const { data: status } = useGoldenDatasetStatus(encounterId, {
    enabled: isCoderAdmin && source !== undefined && supported,
  });

  const membership = source && status?.inDataset ? status : undefined;
  const goldenSource = membership?.source;
  const isGoldenSource =
    source !== undefined && goldenSource != null && goldenSource.kind === source.kind && goldenSource.id === source.id;
  const hasCodes = diagnoses.length + procedures.length > 0;
  const unavailableReason =
    (!supported && "No eval target for this specialty") || (!hasCodes && "No codes to add") || undefined;

  return {
    offersAdd: isCoderAdmin && source !== undefined && !isGoldenSource,
    addLabel: membership ? "Set as golden item codes" : "Add to golden dataset",
    unavailableReason,
    isBusy: add.isPending || remove.isPending,
    membership,
    isGoldenSource,
    add: () => {
      if (!source) return;
      add.mutate({
        encounterId,
        source,
        confirmedCodes: {
          diagnoses: diagnoses.map((item) => item.code),
          procedures: procedures.map((item) => item.code),
        },
      });
    },
    remove: () => remove.mutate(encounterId),
  };
};

export type GoldenDataset = ReturnType<typeof useGoldenDataset>;
